import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useFragment } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import { readInlineData } from 'relay-runtime'

import { useMovementsModal } from '../../hooks/useMovementsModal'
import type { MovementsModal_salesAssociate$key } from './__generated__/MovementsModal_salesAssociate.graphql'
import type { MovementsModal_movementStep$key } from './__generated__/MovementsModal_movementStep.graphql'
import {
  SalesAssociateMovementsTrackingModal,
  SalesAssociateMovementsTrackingModalContent,
  SalesAssociateMovementsTrackingModalItem,
  SalesAssociateMovementsTrackingModalFooter,
  SalesAssociateMovementsTrackingModalHeader,
  SalesAssociateMovementsTrackingModalTitle,
} from '../SalesAssociateMovementsTrackingModal'

export type ModalOptions = {
  onConfirm: (movement: string) => void
  salesAssociate: MovementsModal_salesAssociate$key
  movements: MovementsModal_movementStep$key[]
}

const MovementsModalComponent = () => {
  const intl = useIntl()
  const { close, options, isOpen } = useMovementsModal()

  const [selectedMovementId, setSelectedMovementId] = useState<string | null>(
    null
  )

  const salesAssociate = useFragment<MovementsModal_salesAssociate$key>(
    graphql`
      fragment MovementsModal_salesAssociate on SalesAssociate {
        name
        avatar {
          url
        }
      }
    `,
    options.salesAssociate
  )

  const handleModalClose = () => {
    close()

    setSelectedMovementId(null)
  }

  const handleApplyClick = () => {
    if (selectedMovementId) {
      options.onConfirm(selectedMovementId)
    }

    close()

    setSelectedMovementId(null)
  }

  const handleMovementClick = (clickedMovementId: string) => () => {
    setSelectedMovementId(clickedMovementId)

    if (clickedMovementId === selectedMovementId) {
      handleApplyClick()
    }
  }

  return (
    <SalesAssociateMovementsTrackingModal isOpen={isOpen}>
      <SalesAssociateMovementsTrackingModalHeader
        avatarUrl={salesAssociate.avatar?.url}
        sellerName={salesAssociate.name}
      >
        <SalesAssociateMovementsTrackingModalTitle>
          {intl.messages.exitStoreQuestion}
        </SalesAssociateMovementsTrackingModalTitle>
      </SalesAssociateMovementsTrackingModalHeader>
      <SalesAssociateMovementsTrackingModalContent>
        {options?.movements?.map((movementRef) => {
          const movement = readInlineData(
            graphql`
              fragment MovementsModal_movementStep on SalesAssociateTrackingMovementStep
              @inline {
                id
                description
              }
            `,
            movementRef
          )

          return (
            <SalesAssociateMovementsTrackingModalItem
              key={movement.id}
              onClick={handleMovementClick(movement.id)}
              selected={selectedMovementId === movement.id}
              data-testid="movements-modal-item"
            >
              {movement.description}
            </SalesAssociateMovementsTrackingModalItem>
          )
        })}
      </SalesAssociateMovementsTrackingModalContent>
      <SalesAssociateMovementsTrackingModalFooter
        onCancelClick={handleModalClose}
        onConfirmClick={handleApplyClick}
        shouldDisableConfirmButton={!selectedMovementId}
      />
    </SalesAssociateMovementsTrackingModal>
  )
}

export const MovementsModal = () => {
  const movementsModal = useMovementsModal()

  if (!movementsModal.isOpen || !movementsModal.options.salesAssociate) {
    return null
  }

  return <MovementsModalComponent />
}
